import { Component, OnDestroy, OnInit, signal, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { AgentsService } from '@generated/services/agents.service';
import { AgentSummary, GetAgentsResult } from '@generated/models';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { NotificationService } from '@core/services/notification.service';
import { Signalr } from '@core/services/signalr';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { AssistantContextService } from '@shared/services/assistant-context.service';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';

@Component({
  selector: 'app-agents',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    AssistantActionDirective,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    TooltipModule,
    RouterModule,
    DeleteConfirmationComponent,
    ConfirmDialogModule,
    MenuModule
],
  providers: [ConfirmationService],
  templateUrl: './agents.html',
  styleUrl: './agents.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Agents implements OnInit, OnDestroy {
  private agentsService = inject(AgentsService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private analyticsService = inject(AnalyticsService);
  private notificationService = inject(NotificationService);
  private signalr = inject(Signalr);
  private destroyRef = inject(DestroyRef);
  private assistantContext = inject(AssistantContextService);
  private confirmationService = inject(ConfirmationService);

  agents = signal<AgentSummary[]>([]);
  filteredAgents = signal<AgentSummary[]>([]);
  searchTerm = signal('');
  loading = signal(true);

  constructor() {
    // Пушим контекст только ПОСЛЕ загрузки — иначе popup смонтируется на пустом
    // initial-state, chatResource зафиксирует system без данных (читается один раз).
    effect(() => {
      if (this.loading()) return;
      const agents = this.agents();
      this.assistantContext.set({
        description: 'Agents list',
        value: {
          type: 'agents-list',
          count: agents.length,
          items: agents.map(a => ({ id: a.id, title: a.title })),
        },
      });
    });
  }

  // Delete
  showDeleteDialog = signal(false);
  isDeleting = signal(false);
  agentToDelete: AgentSummary | null = null;

  readonly deleteTitle = $localize`:@@agents.delete.title:Delete Agent`;

  // Actions labels — используются и в mobile-menu (p-menu), и как
  // tooltip'ы в desktop actions. Всегда через i18n, чтобы русская
  // локаль подхватила переводы.
  readonly editActionLabel = $localize`:@@common.edit:Edit`;
  readonly duplicateActionLabel = $localize`:@@agents.duplicate.action:Duplicate`;
  readonly deleteActionLabel = $localize`:@@common.delete:Delete`;
  readonly moreActionsLabel = $localize`:@@common.moreActions:More actions`;

  /** MenuItem'ы для mobile-меню действий одной строки. Дублируют
   *  onclick-логику desktop-кнопок; label'ы обязательны для p-menu. */
  menuItemsFor(agent: AgentSummary): MenuItem[] {
    return [
      {
        label: this.editActionLabel,
        icon: 'pi pi-pencil',
        command: () => this.editAgent(agent),
      },
      {
        label: this.duplicateActionLabel,
        icon: 'pi pi-copy',
        command: () => this.duplicateAgent(agent, new MouseEvent('click')),
      },
      {
        separator: true,
      },
      {
        label: this.deleteActionLabel,
        icon: 'pi pi-trash',
        styleClass: 'menu-item-danger',
        command: () => this.deleteAgent(agent, new MouseEvent('click')),
      },
    ];
  }

  get deleteSubtitle(): string {
    const name = this.agentToDelete?.title || '';
    return $localize`:@@agents.delete.subtitle:Are you sure you want to delete '${name}:name:'? This action cannot be undone.`;
  }

  ngOnInit() {
    this.loadAgents();

    // Бэк рассылает AgentCreated с одним id — проще перезагрузить весь список,
    // чтобы получить title/created_at/created_by_email и т.п. через apiAgentsGet.
    this.signalr.agentCreated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadAgents());
  }

  ngOnDestroy(): void {
    this.assistantContext.clear();
  }

  loadAgents() {
    this.loading.set(true);
    this.agentsService.apiAgentsGet().subscribe({
      next: (result: GetAgentsResult) => {
        const agents = result.agents || [];
        this.agents.set(agents);
        this.filteredAgents.set(agents);
        this.loading.set(false);

        // Track user_created event if user was created
        if (result.user_created) {
          this.analyticsService.trackEvent('user_created');
        }

        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Error loading agents:', error);
        this.loading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  onSearch() {
    const term = this.searchTerm().toLowerCase();
    const filtered = this.agents().filter(agent => 
      (agent.title || '').toLowerCase().includes(term) ||
      (agent.created_by_email || '').toLowerCase().includes(term)
    );
    this.filteredAgents.set(filtered);
  }

  createNewAgent() {
    this.router.navigate(['/agents/new']);
  }

  editAgent(agent: AgentSummary) {
    this.router.navigate(['/agents', agent.id]);
  }

  talkToAgent(agent: AgentSummary, event: Event) {
    event.stopPropagation();
    // Talk-таб внутри agent-edit'а (тот же полный редактор + встроенный
    // тестер), не отдельная страница /agents/:id/test — так у юзера
    // сохраняется контекст редактирования и не приходится ходить туда-обратно.
    this.router.navigate(['/agents', agent.id], { fragment: 'talk' });
  }

  deleteAgent(agent: AgentSummary, event: Event) {
    event.stopPropagation();
    this.agentToDelete = agent;
    this.showDeleteDialog.set(true);
  }

  duplicateAgent(agent: AgentSummary, event: Event) {
    event.stopPropagation();
    const name = agent.title || '';
    this.confirmationService.confirm({
      header: $localize`:@@agents.duplicate.title:Duplicate agent`,
      message: $localize`:@@agents.duplicate.subtitle:Create a copy of '${name}:name:'? A new agent with the same settings will be added.`,
      icon: 'pi pi-copy',
      acceptLabel: $localize`:@@agents.duplicate.confirm:Duplicate`,
      rejectLabel: $localize`:@@common.cancel:Cancel`,
      acceptButtonProps: { severity: 'primary', icon: 'pi pi-copy' },
      rejectButtonProps: { severity: 'secondary', outlined: true },
      accept: () => {
        this.agentsService.apiAgentsDuplicatePost({
          body: { agent_id: agent.id!, new_title: undefined }
        }).subscribe({
          next: () => {
            this.notificationService.showSuccess(
              $localize`:@@agents.duplicate.success:Agent duplicated successfully`
            );
            this.loadAgents();
          },
          error: () => {
            this.notificationService.showError(
              $localize`:@@agents.duplicate.error:Failed to duplicate agent`
            );
          },
        });
      },
    });
  }

  onDeleteConfirm() {
    if (!this.agentToDelete) return;
    this.isDeleting.set(true);
    this.agentsService.apiAgentsIdDelete({ id: this.agentToDelete.id! }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@agents.delete.success:Agent deleted successfully`);
        this.showDeleteDialog.set(false);
        this.isDeleting.set(false);
        this.agentToDelete = null;
        this.loadAgents();
      },
      error: () => {
        this.notificationService.showError($localize`:@@agents.delete.error:Failed to delete agent`);
        this.isDeleting.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
}
